<template>
  <div :class="$attrs.class" class="vc-base-control">
    <div :class="formGroupModifiers" class="c-form-group">
      <template v-if="isField">
        <div :class="controlModifiers" class="c-control">
          <textarea
              v-if="type === 'textarea'"
              :disabled="disabled || computedState === 'processing'"
              :id="id"
              :placeholder="label"
              :value="value"
              @focus="focusIn"
              @focusout="focusOut"
              @input="input"
              class="c-control__input"
              v-bind="attrs"
              v-on="listeners"
          />
          <input
              v-else
              :disabled="disabled || computedState === 'processing'"
              :id="id"
              :placeholder="label"
              :type="type"
              :value="computedValue"
              @focus="focusIn"
              @focusout="focusOut"
              @input="input"
              autocomplete="off"
              class="c-control__input"
              v-bind="attrs"
              v-on="listeners"
          />
          <label :style="{'pointer-events':activeLabel===''?'none':'all'}" :for="activeLabel===null?id:activeLabel" class="c-control__label">{{ label }}</label>
          <span
            :class="rightIconModifiers"
            class="c-control__icon-right"
            v-if="computedRightIcon"
          >
            <slot name="endAdornment">
              <font-awesome-icon
                  :icon="computedRightIcon"
                  :spin="computedState === 'processing'"
              ></font-awesome-icon>
            </slot>
          </span>
        </div>
      </template>
      <template v-else-if="type === 'checkbox'">
        <label :for="id" class="c-checkbox c-checkbox--with-text-label">
          <input
            :checked="value"
            :id="id"
            @change="input"
            type="checkbox"
            v-bind="attrs"
            v-on="listeners"
          />
          <span class="c-checkbox__input"></span>
          <span class="c-checkbox__label"><slot></slot></span>
        </label>
      </template>
      <template v-else-if="type === 'select'">
        <base-select
          :id="id"
          :label="label"
          :name="attrs.name"
          :options="selectOptions"
          :size="size"
          :value="value"
          :selectConfigParams="configParams"
          @input="input"
          @toggle="$emit('toggle', $event)"
        ></base-select>
      </template>
      <template v-else-if="type === 'toggle'">
        <div class="c-toggle">
          <div class="c-toggle__label-left" v-if="leftLabel || !label">
            {{ leftLabel ? leftLabel : "Disabled" }}
          </div>
          <toggle-button
            :height="18"
            :sync="true"
            :value="value || value === 1 ? true : false"
            :width="38"
            :disabled="disabled"
            @change="$emit('change', $event.value)"
            @input="$emit('input', $event)"
            class="c-toggle__button"
          >
          </toggle-button>
          <div class="c-toggle__label">
            <template v-if="label && !(leftLabel || rightLabel)">
              {{ label }}
            </template>
            <template v-else-if="rightLabel || !label">
              {{ rightLabel ? rightLabel : "Enabled" }}
            </template>
          </div>
        </div>
      </template>
      <div
        :class="`c-control-footer--${computedState}`"
        class="c-control-footer"
        v-show="footerMsg || $slots[`footer-${computedState}-msg`]"
      >
        <template v-if="$slots[`footer-${computedState}-msg`]">
          <slot :name="`footer-${computedState}-msg`"></slot>
        </template>
        <template v-else>
          {{ footerMsg }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import validate from "validate.js";
import BaseSelect from "./base-select.vue";
import { ToggleButton } from "vue-js-toggle-button";

export default {
  inheritAttrs: false,
  name: "base-control",
  props: {
    configParams: {
      type: Object
    },
    id: {
      type: String,
    },
    triggerValidation: {
      type: Boolean,
      default: false
    },
    leftLabel: {
      type: String
    },
    rightLabel: {
      type: String
    },
    label: {
      type: String
    },
    value: {
      type: undefined
    },
    type: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      validator: function(value) {
        return ["small", "medium", "large"].indexOf(value) !== -1;
      }
    },
    errorMsg: {
      type: String
    },
    successMsg: {
      type: String
    },
    infoMsg: {
      type: String
    },
    processingMsg: {
      type: String
    },
    processingWithIcon: {
      type: Boolean,
      default: true
    },
    successWithIcon: {
      type: Boolean,
      default: false
    },
    errorWithIcon: {
      type: Boolean,
      default: false
    },
    state: {
      type: String,
      default: "info",
      validator: function(value) {
        return ["info", "processing", "error", "success"].indexOf(value) !== -1;
      }
    },
    rightIcon: {
      type: [String, Array]
    },
    constraints: {
      type: Object
    },
    selectOptions: {
      type: Array
    },
    numberMax: {
      type: Number,
    },
    numberSuffix: {
      type: String
    }
  },
  components: {
    BaseSelect,
    ToggleButton
  },
  mounted() {
    if (this.triggerValidation) this.runInstantValidation();
  },
  data() {
    return {
      active: false,
      activeLabel:null,
      validated: false,
      validationOnInput: false,
      validationError: null,
      typingPaused: false
    };
  },
  computed: {
    isField() {
      return (
        ["text", "email", "number", "password", "search", "tel", "url", "textarea"].indexOf(
          this.type
        ) !== -1
      );
    },
    computedValue() {
      if (!this.numberSuffix) {
        return this.value
      }
      if (this.active) {
        return this.unmaskNumber(this.value.toString())
      } else {
        return this.maskNumber(this.value.toString(), this.numberSuffix)
      }
    },
    computedState() {
      return this.constraints && this.validationError ? "error" : this.state;
    },
    attrs() {
      let attrs = Object.assign({}, this.$attrs);
      delete attrs.class;
      delete attrs.placeholder;
      delete attrs.id;
      delete attrs.value;
      delete attrs.disabled;
      delete attrs.autocomplete;
      delete attrs.type;
      return attrs;
    },
    listeners() {
      let listeners = Object.assign({}, this.$listeners);
      if (this.type === "checkbox") {
        delete listeners.change;
      } else {
        delete listeners.focusout;
      }
      delete listeners.input;

      return listeners;
    },
    controlModifiers() {
      return {
        "c-control--small": this.small,
        "c-control--medium": this.size === "medium",
        "c-control--disabled": this.disabled,
        "c-control--processing": this.computedState === "processing"
      };
    },
    formGroupModifiers() {
      return {
        "c-form-group--info": this.computedState === "info",
        "c-form-group--error": this.computedState === "error",
        "c-form-group--success": this.computedState === "success"
      };
    },
    footerMsg() {
      switch (this.computedState) {
        case "error":
          return this.validationError || this.errorMsg;
        case "success":
          return this.successMsg;
        case "processing":
          return this.processingMsg;
        case "info":
          return this.infoMsg;
      }
    },
    rightIconModifiers() {
      return this.computedRightIcon !== this.rightIcon
        ? `c-control__icon-right--${this.computedState}`
        : null;
    },
    computedRightIcon() {
      if (this.computedState === "processing" && this.processingWithIcon)
        return "spinner-third";
      else if (this.computedState === "success" && this.successWithIcon)
        return "check-circle";
      else if (this.computedState === "error" && this.errorWithIcon)
        return "times-circle";
      else return this.rightIcon;
    }
  },
  methods: {
    input(event) {
      let value;
      switch (this.type) {
        case "checkbox":
          value = event.target.checked;
          if (this.constraints) this.validate(value);
          this.$emit("change", event);
          break;
        case "select":
          value = event;
          if (this.constraints) this.validate(value);
          break;
        default:
          value = event.target.value;
          if (this.constraints && this.validationOnInput) this.validate(value);

          /**
           * Number suffix: e.g 365 days
           * */
          if(this.numberSuffix) {
            if (value) {
              value = +this.unmaskNumber(value)
              if (this.numberMax && value > this.numberMax) {
                value = this.numberMax
              }
              event.target.value = value
            } else {
              value = 0
            }
          }
      }

      this.$emit("input", value);
    },
    focusIn(){
        this.active = true;
        this.activeLabel="";
    },
    focusOut(event) {
      this.active = false;
      if (this.constraints && !this.validationOnInput)
        this.runInstantValidation();
      this.$emit("focusout", event);

      if(this.value==="")
        this.activeLabel=this.id;
      else this.activeLabel="";

    },
    validate(value) {
      const errors = validate.single(
        value !== undefined ? value : this.value,
        this.constraints
      );
      this.validationError =
        errors && errors.length > 0 ? validate.capitalize(errors[0]) : null;
      this.validated = !this.validationError;
      this.$emit("validated", this.validated);
    },
    runInstantValidation() {
      this.validate();
      this.validationOnInput = true;
    },
    maskNumber(content, suffix) {
      return `${this.unmaskNumber(content)}${suffix}`
    },
    unmaskNumber(content) {
      return content.replace(/[^0-9]/g, '')
    }
  },
  watch: {
    triggerValidation(force) {
      if (force) this.runInstantValidation();
    }
  },
  created(){
    //console.log("created:",'"'+this.label+'"', this.id);
    //this.activeLabel = this.id;
  },
  destroyed(){
    //console.log("destroyed:",'"'+this.label+'"', this.id);
  }
};
</script>

<style lang="scss" scoped>
// Temp import to prevent the app.vue styles from overriding c-control styles
@import "../../../../../sass/embeds/infinity/store/components/form";

.vc-base-control {
  .c-checkbox {
    margin-bottom: 0;
  }
}
</style>
